package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.SensitiveUserData
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import kotlinx.coroutines.flow.Flow
import com.evoapps.evoshift.domain.model.Device
import com.evoapps.evoshift.domain.model.Notification

interface UserRepository {
    // User Profile
    suspend fun getUserProfile(uid: String): Result<User>
    /** Display name and setup flag; the photo has its own flow (ProfilePhotoRepository, SH-94). */
    suspend fun updateUserProfile(user: User): Result<Unit>

    /**
     * SH-137/SH-138: the person's own app language on their profile: [language] the one the app
     * shows ("en", "pl", "uk"), [preference] the one picked in the app (null: the device's).
     */
    suspend fun updateLanguage(uid: String, language: String, preference: String?): Result<Unit>

    /** SH-196: the person's own app colours on their profile (users/{uid}.appearance). */
    suspend fun updateAppearance(uid: String, appearance: Appearance): Result<Unit>

    // Engagement Management (Manager Only)
    suspend fun addEngagement(userId: String, engagement: Engagement): Result<Unit>

    /**
     * SH-192: [engagement] added to an existing person (someone of another workplace of the
     * caller) together with that workplace's pay terms (users/{uid}/sensitive/{workplaceId}) in
     * one batch: firestore.rules accept the pay terms only for an engagement the write leaves on
     * the profile. [financial] null: the engagement only (no right to pay data). The person's
     * other engagements and their contact details ('general') stay as they are.
     */
    suspend fun addEngagementWithPayTerms(userId: String, engagement: Engagement, financial: FinancialEngagement?): Result<Unit>
    suspend fun terminateEngagement(userId: String, workplaceId: String): Result<Unit>

    // Listing Employees (For Dropdowns)
    suspend fun getAssignableEmployees(workplaceId: String): Result<List<User>>

    // Push Notifications
    suspend fun saveDeviceToken(uid: String, device: Device): Result<Unit>
    suspend fun removeDeviceToken(uid: String, token: String): Result<Unit>
    suspend fun getUserDevices(uid: String): Result<List<Device>>

    // Sensitive Data (Manager Only)
    suspend fun getSensitiveUserData(uid: String): Result<SensitiveUserData>

    /**
     * SH-250: [uid]'s pay terms at [workplaceId] (users/{uid}/sensitive/{wpId}) as they change,
     * edits of this device first (offline persistence); null: none or not readable. Without a
     * live source (test fakes): nothing is emitted.
     */
    fun observeFinancialEngagement(uid: String, workplaceId: String): Flow<FinancialEngagement?> = kotlinx.coroutines.flow.emptyFlow()
    /** [sensitiveData] null: the profile only (the caller may not write pay or personal data). */
    suspend fun createEmployee(user: User, sensitiveData: SensitiveUserData?): Result<Unit>

    /** [includeSensitive] false: profile and engagement only, the sensitive documents stay as they are. */
    suspend fun updateEmployeeDetailsFull(
        userId: String,
        workplaceId: String,
        workplaceName: String,
        displayName: String,
        email: String?,
        role: String,
        skills: List<String>,
        permission: UserPermission,
        isPrimary: Boolean,
        financial: FinancialEngagement,
        phoneNumber: String?,
        address: String?,
        bankAccountNumber: String?,
        includeSensitive: Boolean = true,
        /** Competence (SH-47): part of the engagement, like role and skills. */
        competence: Competence = Competence.INDEPENDENT
    ): Result<Unit>

    suspend fun updateCachedWorkplaceName(
        userId: String,
        workplaceId: String,
        newName: String
    ): Result<Unit>

    suspend fun sendNotification(notification: Notification): Result<Unit>
    fun observeUnreadNotificationsCount(userId: String): Flow<Int>
    /** The user's notifications, newest first. */
    fun observeNotifications(userId: String, limit: Int = 50): Flow<List<Notification>>
    suspend fun markNotificationsRead(userId: String, notificationIds: Collection<String>): Result<Unit>
}
